import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "./App.css";
import "./mobile.css";
import HomePage from "./pages/HomePage";
import BotsPage from "./pages/BotsPage";
import BotDetailsPage from "./pages/BotDetailsPage";
import AuthCallback from "./pages/AuthCallback";
import AddBotPage from "./pages/AddBotPage";
import EditBotPage from "./pages/EditBotPage";
import ProfilePage from "./pages/ProfilePage";
import SettingsPage from "./pages/SettingsPage";
import NotificationsPage from "./pages/NotificationsPage";
import ScrollToHash from "./components/ScrollToHash";
import UserProfile from "./components/UserProfile";
import NotificationBell from "./components/NotificationBell";
import PricingPage from "./pages/PricingPage";
import CheckoutPage from "./pages/CheckoutPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import GuidelinesPage from "./pages/GuidelinesPage";
import CookiesPage from "./pages/CookiesPage";
import FaqPage from "./pages/FaqPage";
import PartnersPage from "./pages/PartnersPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import PartnerLandingPage from "./pages/PartnerLandingPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import CookieConsent from "./components/CookieConsent";
import authService from "./services/discordAuth";
import { trackPageView, setAnalyticsUserId } from "./utils/analytics";
import "./pages/AuthCallback.css";
import BotDashboard from "./pages/BotDashboard";

import { useTranslation } from "react-i18next";

// Componente para rastrear visualizações de página em SPAs
function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search);

    // Se o usuário estiver logado, vincula o ID dele ao Analytics
    if (authService.isAuthenticated()) {
      const user = authService.getUser();
      if (user && user.id) {
        setAnalyticsUserId(user.id);
      }
    }
  }, [location]);

  return null;
}

function App() {
  const { t } = useTranslation();

  useEffect(() => {
    // Validar sessão ao carregar o app
    const validateSession = async () => {
      if (authService.isAuthenticated()) {
        await authService.fetchUser();
      }
    };

    validateSession();

    // Smooth scroll para links de navegação
    document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
      anchor.addEventListener("click", function (e) {
        e.preventDefault();
        const target = document.querySelector(this.getAttribute("href"));
        if (target) {
          target.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      });
    });

    // Navbar transparente ao rolar
    const navbar = document.querySelector(".navbar");
    const handleScroll = () => {
      const currentScroll = window.pageYOffset;

      if (currentScroll > 100) {
        navbar.style.background = "rgba(15, 17, 23, 0.98)";
        navbar.style.boxShadow = "0 2px 20px rgba(0, 0, 0, 0.3)";
      } else {
        navbar.style.background = "rgba(15, 17, 23, 0.95)";
        navbar.style.boxShadow = "none";
      }
    };

    window.addEventListener("scroll", handleScroll);

    // Efeito de partículas no hero
    const createParticles = () => {
      const hero = document.querySelector(".hero-background");
      if (!hero) return;

      const particleCount = 50;

      for (let i = 0; i < particleCount; i++) {
        const particle = document.createElement("div");
        particle.className = "particle";
        particle.style.cssText = `
          position: absolute;
          width: 2px;
          height: 2px;
          background: rgba(88, 101, 242, 0.5);
          border-radius: 50%;
          top: ${Math.random() * 100}%;
          left: ${Math.random() * 100}%;
          animation: float ${3 + Math.random() * 4}s ease-in-out infinite;
          animation-delay: ${Math.random() * 2}s;
        `;
        hero.appendChild(particle);
      }
    };

    createParticles();

    // Observador de interseção para animações
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -100px 0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = "1";
          entry.target.style.transform = "translateY(0)";
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    // Observa elementos para animação
    setTimeout(() => {
      document.querySelectorAll(".feature-card, .bot-card, .stat-box").forEach((el) => {
        el.style.opacity = "0";
        el.style.transform = "translateY(30px)";
        el.style.transition = "opacity 0.6s ease, transform 0.6s ease";
        observer.observe(el);
      });
    }, 100);

    // Adiciona efeito de hover nos cards
    const addHoverEffects = () => {
      document.querySelectorAll(".bot-card, .feature-card").forEach((card) => {
        card.addEventListener("mouseenter", function () {
          this.style.transform = "translateY(-10px) scale(1.02)";
        });

        card.addEventListener("mouseleave", function () {
          this.style.transform = "translateY(0) scale(1)";
        });
      });
    };

    setTimeout(addHoverEffects, 100);

    // Fechar menu mobile ao clicar em um link (para mobile)
    const navbarCollapse = document.getElementById("navbarNav");
    const handleMenuClick = (e) => {
      // Verifica se o clique foi em um link ou dentro do UserProfile/NotificationBell que deve fechar o menu
      const isNavLink = e.target.classList.contains("nav-link") || e.target.closest(".nav-link");
      const isActionItem = e.target.closest(".btn") || e.target.closest(".notification-bell");

      if ((isNavLink || isActionItem) && navbarCollapse.classList.contains("show")) {
        const toggler = document.querySelector(".navbar-toggler");
        if (toggler) toggler.click();
      }
    };

    if (navbarCollapse) {
      navbarCollapse.addEventListener("click", handleMenuClick);
    }

    // Console easter egg
    console.log(
      "%c🤖 BotGate",
      "font-size: 50px; font-weight: bold; background: linear-gradient(135deg, #5865F2 0%, #EB459E 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent;",
    );
    console.log("%cOlá, desenvolvedor! 👋", "font-size: 20px; color: #5865F2;");
    console.log("%cInteressado em contribuir? Entre no nosso Discord!", "font-size: 14px; color: #b9bbbe;");
    console.log("%c", "font-size: 14px; color: #b9bbbe;", "https://www.discord.gg/xK4r9HqKKf");

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (navbarCollapse) {
        navbarCollapse.removeEventListener("click", handleMenuClick);
      }
    };
  }, []);

  return (
    <Router>
      <AnalyticsTracker />
      <div className="App">
        {/* Navbar Bootstrap Responsivo */}
        <nav className="navbar navbar-expand-lg navbar-dark fixed-top">
          <div className="container">
            <Link className="navbar-brand d-flex align-items-center" to="/">
              <img src="/logo.png" alt="BotGate Logo" className="logo me-2" style={{ width: "40px", height: "40px", borderRadius: "50%" }} />
              <span className="brand-name">BotGate</span>
            </Link>

            <button
              className="navbar-toggler"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#navbarNav"
              aria-controls="navbarNav"
              aria-expanded="false"
              aria-label="Toggle navigation"
            >
              <span className="navbar-toggler-icon"></span>
            </button>

            <div className="collapse navbar-collapse" id="navbarNav">
              <ul className="navbar-nav ms-auto mb-2 mb-lg-0 d-md-flex flex-row justify-content-between">
                <li className="nav-item">
                  <Link className="nav-link" to="/">
                    {t("navbar.home")}
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link" to="/bots">
                    {t("navbar.bots")}
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link" to="/pricing">
                    {t("navbar.plans")}
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link" to="/blog">
                    {t("navbar.blog")}
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link" to="/partners">
                    {t("navbar.partners")}
                  </Link>
                </li>
                <li className="nav-item d-block d-lg-none">
                  <Link className="nav-link" to="/profile">
                    {t("navbar.profile")}
                  </Link>
                </li>
                <li className="nav-item d-block d-lg-none">
                  <Link className="nav-link" to="/notifications">
                    {t("navbar.notifications")}
                  </Link>
                </li>
                <li className="nav-item">
                  <NotificationBell />
                </li>
              </ul>
              <div className="d-flex gap-2 align-items-center mt-3 mt-lg-0 ms-lg-3">
                <UserProfile />
                <a href="https://www.discord.gg/xK4r9HqKKf" className="btn btn-primary btn-sm">
                  {t("navbar.support")}
                </a>
              </div>
            </div>
          </div>
        </nav>

        {/* Scroll automático para hash */}
        <ScrollToHash />

        {/* Routes */}
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/bots" element={<BotsPage />} />
          <Route path="/bots/:botId" element={<BotDetailsPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/add-bot" element={<AddBotPage />} />
          <Route path="/edit-bot/:botId" element={<EditBotPage />} />
          <Route path="/profile/:userId" element={<ProfilePage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/checkout/:tierId" element={<CheckoutPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/guidelines" element={<GuidelinesPage />} />
          <Route path="/cookies" element={<CookiesPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/partners" element={<PartnersPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/p/:partnerName" element={<PartnerLandingPage />} />
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/dashboard/:botId/:guildId" element={<BotDashboard />} />
          {/* Vanity URL (deve ser a última para não conflitar com rotas fixas) */}
          <Route path="/:botId" element={<BotDetailsPage />} />
        </Routes>

        {/* Footer */}
        <footer className="footer">
          <div className="container">
            <div className="footer-grid">
              <div className="footer-col">
                <h4>BotGate</h4>
                <p>{t("footer.description")}</p>
                <div className="social-links">
                  <a href="https://www.discord.gg/xK4r9HqKKf" className="social-link" target="_blank" rel="noopener noreferrer">
                    <span>💬</span> {t("footer.discordServer")}
                  </a>
                </div>
              </div>
              <div className="footer-col">
                <h4>{t("footer.quickLinks")}</h4>
                <ul>
                  <li>
                    <Link to="/">{t("navbar.home")}</Link>
                  </li>
                  <li>
                    <Link to="/bots">{t("navbar.bots")}</Link>
                  </li>
                  <li>
                    <Link to="/pricing">{t("footer.plans") || t("navbar.plans")}</Link>
                  </li>
                  <li>
                    <Link to="/blog">{t("navbar.blog")}</Link>
                  </li>
                  <li>
                    <Link to="/partners">{t("navbar.partners")}</Link>
                  </li>
                  <li>
                    <a href="https://docs.botgate.com.br/" target="_blank" rel="noopener noreferrer">
                      {t("footer.devs")}
                    </a>
                  </li>
                </ul>
              </div>
              <div className="footer-col">
                <h4>{t("footer.legal")}</h4>
                <ul>
                  <li>
                    <Link to="/terms">{t("footer.terms")}</Link>
                  </li>
                  <li>
                    <Link to="/privacy">{t("footer.privacy")}</Link>
                  </li>
                  <li>
                    <Link to="/guidelines">{t("footer.guidelines")}</Link>
                  </li>
                  <li>
                    <Link to="/cookies">{t("footer.cookies")}</Link>
                  </li>
                </ul>
              </div>
              <div className="footer-col">
                <h4>{t("footer.social")}</h4>
                <ul>
                  <li>
                    <a href="https://www.instagram.com/botgateoficial" target="_blank" rel="noopener noreferrer">
                      Instagram
                    </a>
                  </li>
                  <li>
                    <a href="https://www.youtube.com/@BotGateList" target="_blank" rel="noopener noreferrer">
                      Youtube
                    </a>
                  </li>
                  <li>
                    <a href="https://www.tiktok.com/@botgateoficial" target="_blank" rel="noopener noreferrer">
                      TikTok
                    </a>
                  </li>
                </ul>
              </div>
              <div className="footer-col">
                <h4>{t("footer.support")}</h4>
                <ul>
                  <li>
                    <Link to="/faq">{t("footer.faq")}</Link>
                  </li>
                  <li>
                    <a href="https://www.discord.gg/xK4r9HqKKf" target="_blank" rel="noopener noreferrer">
                      {t("footer.supportServer")}
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            <div className="footer-bottom">
              <p>&copy; 2026 BotGate. {t("footer.rights")}</p>
            </div>
          </div>
        </footer>
        <CookieConsent />
      </div>
    </Router>
  );
}

export default App;
